/**
 * Which days, months and years a date picker may offer.
 *
 * Pulled out of TapDatePicker.vue so it can be tested: the component needs
 * Vuetify and a DOM, and this repo mounts neither, so anything left inside it
 * is checked by clicking. Every rule here is one a wrong answer breaks
 * silently — a disabled day still renders, an absent month just isn't in a
 * list — which is the kind that wants a test rather than an eye.
 *
 * The component's own defaults are for SCHEDULING (nothing before today). A
 * question about the past has to say so; see dateOfBirthWindow in
 * useAccountSetup for the shape of that.
 *
 * House style: imported explicitly, never auto-imported.
 */
import {DateTime} from 'luxon';

/** How far a select reaches when a side of the window is open-ended. */
export const OPEN_ENDED_YEARS = 10;

const startOfDay = (d) => (d == null ? null : d.startOf('day'));

/** A day outside the window, whichever edge it falls past. */
export function dayOutOfRange(day, min, max) {
  const lo = startOfDay(min);
  const hi = startOfDay(max);
  return (lo != null && day < lo) || (hi != null && day > hi);
}

/** Whether a whole month holds at least one day that can be picked. */
export function monthHasAnyDay(year, month, min, max) {
  const start = DateTime.fromObject({year, month, day: 1});
  const end = start.endOf('month').startOf('day');
  const lo = startOfDay(min);
  const hi = startOfDay(max);
  if (lo != null && end < lo) return false;
  if (hi != null && start > hi) return false;
  return true;
}

/**
 * The months worth offering in a given year. Filtered rather than disabled, so
 * the select cannot land the grid on a month where nothing is selectable.
 */
export function monthsToOffer(year, min, max) {
  const months = [];
  for (let m = 1; m <= 12; m += 1) {
    if (monthHasAnyDay(year, m, min, max)) {
      months.push({value: m, label: DateTime.fromObject({year, month: m, day: 1}).toFormat('LLLL')});
    }
  }
  return months;
}

/**
 * The years worth offering.
 *
 * Newest first when the window looks backwards (a birthday), oldest first when
 * it looks forwards (a fixture), so the year wanted is near the top either way.
 *
 * Always stretched to cover the year on screen and the year already chosen.
 * With no bounds the range is only a decade either side of now, and the arrows
 * can walk past it — a year with no matching option renders the select EMPTY,
 * which would blank the control exactly when someone had navigated on purpose.
 */
export function yearsToOffer({min, max, viewYear, selectedYear, now = DateTime.now()}) {
  const lo = min?.year ?? (max ? max.year - OPEN_ENDED_YEARS : now.year - OPEN_ENDED_YEARS);
  const hi = max?.year ?? (min ? min.year + OPEN_ENDED_YEARS : now.year + OPEN_ENDED_YEARS);
  const mustInclude = [viewYear, selectedYear].filter((y) => y != null);
  const from = Math.min(lo, hi, ...mustInclude);
  const to = Math.max(lo, hi, ...mustInclude);
  const years = [];
  for (let y = from; y <= to; y += 1) years.push(y);
  return to <= now.year ? years.reverse() : years;
}

/**
 * Where the grid should actually go for a requested year and month.
 *
 * Changing the year can strand the month — asking for December in a window
 * that ends in June — so it moves to the nearest month that year does offer.
 * Null when that year has nothing at all, which the caller reads as "don't
 * move", never as "move somewhere blank".
 */
export function monthToShow(year, month, min, max) {
  const allowed = [];
  for (let m = 1; m <= 12; m += 1) {
    if (monthHasAnyDay(year, m, min, max)) allowed.push(m);
  }
  if (allowed.length === 0) return null;
  const nearest = allowed.includes(month)
    ? month
    : allowed.reduce((best, m) => (Math.abs(m - month) < Math.abs(best - month) ? m : best), allowed[0]);
  return {year, month: nearest};
}

/** Whether ‹ or › would land anywhere with a day to pick. */
export function canStep(viewMonth, months, min, max) {
  const target = viewMonth.plus({months});
  return monthHasAnyDay(target.year, target.month, min, max);
}

/**
 * The quick chips that still make sense. A chip is a count of days from today,
 * so one landing outside the window is HIDDEN rather than disabled — "Today"
 * greyed out on a birthday field is a question nobody asked.
 */
export function chipsToOffer(chips, min, max, now = DateTime.now()) {
  const from = now.startOf('day');
  return (chips ?? []).filter((chip) => !dayOutOfRange(from.plus({days: chip.days}), min, max));
}

/**
 * The month the popover opens on with nothing chosen yet. Today, unless today
 * is outside the window — a picker opening on a month where every day is
 * disabled, with no explanation, reads as broken.
 */
export function monthToOpenOn({selected, min, max, now = DateTime.now()}) {
  if (selected != null) return selected.startOf('month');
  const from = now.startOf('day');
  const hi = startOfDay(max);
  const lo = startOfDay(min);
  if (hi != null && from > hi) return hi.startOf('month');
  if (lo != null && from < lo) return lo.startOf('month');
  return from.startOf('month');
}

/**
 * Where a date-of-birth picker starts. An existing answer on itself; with none,
 * about 25 years back, clamped into the window. An empty picker that opened on
 * today made today the easiest pick, and test accounts kept registering as
 * born yesterday. A UX default only, never an age rule, and nothing is saved
 * until the member picks.
 *
 * Mirrors the app's dateOfBirthPickerStart (account_setup.dart).
 */
export function dateOfBirthPickerStart({selected = null, min = null, max = null, now = DateTime.now()}) {
  let start = selected ?? now.startOf('day').minus({years: 25});
  const lo = startOfDay(min);
  const hi = startOfDay(max);
  if (lo != null && start < lo) start = lo;
  if (hi != null && start > hi) start = hi;
  return start;
}

/** A date of birth with no answer yet opens on the years, not on a month grid. */
export function opensOnYears({birthDate, selected}) {
  return Boolean(birthDate) && selected == null;
}
